import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  editCallAgentRedirect,
  newCallAgentRedirect,
  redirectToCallAgentsPage,
} from '../../helpers/agentHelper';
import { notifyCalendarOAuthResult } from '../../helpers/embedMessaging';
import { useSnackBar } from '../../hooks/useSnackBar';
import { useTranslation } from '../../hooks/useTranslation';
import { ResponseObject, WorkflowType } from '../../interfaces/App';
import { CalendarStateData } from '../../interfaces/Calendar';
import apiRequest from '../../services/apiService';

const CalendarCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [stateData, setStateData] = useState<CalendarStateData | undefined>();
  const showSnackBar = useSnackBar();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const finishCalendarOAuth = (
    success: boolean,
    onRedirect: () => void,
    errorMessage?: string
  ) => {
    const isPopupFlow =
      sessionStorage.getItem('calendar_oauth_popup') === '1' ||
      (window.opener && !window.opener.closed);

    sessionStorage.removeItem('calendar_oauth_popup');

    if (isPopupFlow) {
      notifyCalendarOAuthResult({
        success,
        error: errorMessage,
      });
      window.close();
      return;
    }

    onRedirect();
  };

  useEffect(() => {
    // Create a unique key for this callback session based on the auth code
    const code = searchParams.get('code');
    const callbackKey = `calendar_callback_processed_${code}`;

    if (sessionStorage.getItem(callbackKey)) {
      // If we've already processed this callback, try to redirect
      // This handles the case where component remounts after processing
      const state = searchParams.get('state');
      if (state) {
        try {
          const parsedStateData = JSON.parse(decodeURIComponent(state));
          redirectToCallAgentsPage(navigate, parsedStateData);
        } catch (parseError) {
          console.warn('Failed to parse state for redirect:', parseError);
          newCallAgentRedirect(navigate);
        }
      }
      return;
    }

    const handleCallback = async () => {
      // Mark as processed before making the API call to prevent race conditions
      sessionStorage.setItem(callbackKey, 'true');

      try {
        // Get the authorization code from URL parameters
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        const state = searchParams.get('state');

        // Parse state data early
        let parsedStateData: CalendarStateData | undefined;
        if (state) {
          try {
            parsedStateData = JSON.parse(decodeURIComponent(state));
            setStateData(parsedStateData);
          } catch (parseError) {
            console.warn('Failed to parse state parameter:', parseError);
          }
        }

        if (error) {
          const errorMsg = errorDescription || error;
          setError(errorMsg);
          showSnackBar(
            t('Calendar connection failed: {{error}}', { error: errorMsg }),
            'error'
          );
          setTimeout(() => {
            finishCalendarOAuth(
              false,
              () => {
                if (parsedStateData?.workflow === WorkflowType.CREATE) {
                  newCallAgentRedirect(navigate);
                } else {
                  editCallAgentRedirect(navigate, parsedStateData?.id);
                }
              },
              errorMsg
            );
          }, 3000);
          return;
        }

        if (!code) {
          const errorMsg = t('No authorization code received');
          setError(errorMsg);
          showSnackBar(
            t('Calendar connection failed: {{error}}', { error: errorMsg }),
            'error'
          );
          setTimeout(() => {
            finishCalendarOAuth(
              false,
              () => {
                if (parsedStateData?.workflow === WorkflowType.CREATE) {
                  newCallAgentRedirect(navigate);
                } else {
                  editCallAgentRedirect(navigate, parsedStateData?.id);
                }
              },
              errorMsg
            );
          }, 3000);
          return;
        }

        if (!parsedStateData?.id) {
          const errorMsg = t('No agent ID provided');
          setError(errorMsg);
          showSnackBar(
            t('Calendar connection failed: {{error}}', { error: errorMsg }),
            'error'
          );
          setTimeout(() => {
            finishCalendarOAuth(
              false,
              () => {
                newCallAgentRedirect(navigate);
              },
              errorMsg
            );
          }, 3000);
          return;
        }

        if (!parsedStateData?.app) {
          const errorMsg = t('No calendar provider specified');
          setError(errorMsg);
          showSnackBar(
            t('Calendar connection failed: {{error}}', { error: errorMsg }),
            'error'
          );
          setTimeout(() => {
            finishCalendarOAuth(
              false,
              () => {
                if (parsedStateData?.workflow === WorkflowType.CREATE) {
                  newCallAgentRedirect(navigate);
                } else {
                  editCallAgentRedirect(navigate, parsedStateData?.id);
                }
              },
              errorMsg
            );
          }, 3000);
          return;
        }

        // Send the authorization code to backend for token exchange
        const response: ResponseObject = await apiRequest(
          'agent',
          'POST',
          '/external_capabilities/connect_and_store_creds',
          {
            code: code,
            app: parsedStateData.app,
            id: parsedStateData.id,
            expiration_days: parsedStateData.expirationDays,
          }
        );

        // Fix: apiRequest returns parsed data, not Response object
        if (response && response.status === 'success') {
          // Show success toast message
          const calendarProvider =
            parsedStateData.app === 'google'
              ? 'Google Calendar'
              : parsedStateData.app === 'outlook'
                ? 'Microsoft Outlook'
                : parsedStateData.app;
          showSnackBar(
            t('{{provider}} calendar connected successfully!', {
              provider: calendarProvider,
            }),
            'success'
          );

          // Set calendar as connected in localStorage
          const { setCalendarAuthState } = await import(
            '../../services/calendarService'
          );

          // For CREATE workflow, use the current placeholderAgentId from sessionStorage
          // to ensure the calendar auth state matches what the form expects
          if (parsedStateData?.workflow === WorkflowType.CREATE) {
            const currentPlaceholderAgentId =
              sessionStorage.getItem('placeholderAgentId');
            if (currentPlaceholderAgentId) {
              setCalendarAuthState(currentPlaceholderAgentId, true);
            }
          } else {
            // For EDIT workflow, use the agent ID from the state
            setCalendarAuthState(parsedStateData.id, true);
          }

          // Redirect back to the agent form at step 2 (capabilities)
          setTimeout(() => {
            sessionStorage.removeItem(callbackKey);
            finishCalendarOAuth(true, () => {
              redirectToCallAgentsPage(navigate, parsedStateData);
            });
          }, 2000);
        } else {
          throw new Error('Calendar authorization failed');
        }
      } catch (err) {
        console.error('Error handling calendar callback:', err);
        const errorMsg = 'Failed to complete calendar authorization';
        setError(errorMsg);
        showSnackBar(
          t('Calendar connection failed: {{error}}', { error: errorMsg }),
          'error'
        );
        // Clean up the callback processing flag on error
        sessionStorage.removeItem(callbackKey);
        setTimeout(() => {
          const state = searchParams.get('state');
          let parsedStateData:
            | { id?: string; app?: string; workflow?: WorkflowType }
            | undefined;
          if (state) {
            try {
              parsedStateData = JSON.parse(decodeURIComponent(state));
            } catch (parseError) {
              console.warn('Failed to parse state parameter:', parseError);
            }
          }

          finishCalendarOAuth(
            false,
            () => {
              redirectToCallAgentsPage(navigate, parsedStateData);
            },
            errorMsg
          );
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
      }}
    >
      {error ? (
        <>
          <Typography variant="h6" color="error" gutterBottom>
            {t('Calendar Authorization Failed')}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {stateData?.workflow === WorkflowType.CREATE
              ? t('Redirecting back to agent creation...')
              : t('Redirecting back to agent edit...')}
          </Typography>
        </>
      ) : (
        <>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {t('Connecting Calendar...')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('Please wait while we complete the calendar authorization.')}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default CalendarCallback;
